/**
 * Frontend Primitives - Core Types
 *
 * Mirrors backend primitives with TypeScript types.
 * Follows 2026 standards: snake_case, LocalizedText everywhere, composition.
 *
 * Design principles:
 * - LocalizedText for all user-facing strings (ja, en, ru)
 * - snake_case naming convention (matches backend)
 * - Readonly interfaces for immutability
 * - Type guards for runtime validation
 */

import type { SupportedLanguage } from "@/shared/i18n/types";

// ===================
// Core Primitives
// ===================

/**
 * Localized text in multiple languages.
 * Keys match SupportedLanguage type for consistency.
 */
export interface LocalizedText {
  readonly ja: string;
  readonly en: string;
  readonly ru: string;
}

/**
 * Locale type derived from LocalizedText keys.
 * Single source of truth for locale consistency.
 */
export type Locale = keyof LocalizedText;

/**
 * Type assertion: Locale must match SupportedLanguage.
 * Ensures i18n system and LocalizedText stay in sync.
 * Note: This type is for compile-time checking only.
 */
export type AssertLocaleSync = Locale extends SupportedLanguage
  ? SupportedLanguage extends Locale
    ? true
    : "SupportedLanguage has keys not in LocalizedText"
  : "LocalizedText has keys not in SupportedLanguage";

/**
 * Get localized text for current locale.
 * No fallback - all locales are required.
 */
export function getLocalized(text: LocalizedText, locale: Locale): string {
  return text[locale];
}

/**
 * Currency with amount and currency code.
 */
export interface Currency {
  readonly amount: number;
  readonly currency: "JPY";
}

/**
 * Geographic coordinates (latitude/longitude).
 */
export interface GeoPoint {
  readonly lat: number;
  readonly lng: number;
}

/**
 * Audit timestamps (ISO 8601 format).
 */
export interface TimestampsBlock {
  readonly created_at: string;
  readonly updated_at: string;
}

// ===================
// Location Components
// ===================

/**
 * Nearest station information.
 */
export interface StationInfo {
  readonly name: LocalizedText;
  readonly walk_minutes: number;
  readonly distance_m?: number;
  readonly line_name?: LocalizedText;
  readonly line_code?: string;
  readonly line_color?: string;
}

/**
 * Location information aggregate.
 */
export interface LocationBlock {
  readonly prefecture: LocalizedText;
  readonly city: LocalizedText;
  readonly ward?: LocalizedText;
  readonly address_display: LocalizedText;
  readonly stations: readonly StationInfo[];
  readonly coordinates?: GeoPoint;
}

// ===================
// Financials Components
// ===================

/**
 * Financial information aggregate.
 */
export interface FinancialsBlock {
  readonly rent: Currency;
  readonly management_fee?: Currency;
  readonly deposit?: Currency;
  readonly key_money?: Currency;
  readonly guarantor_required: boolean;
  readonly renewal_fee?: Currency;
  readonly insurance_fee?: Currency;
}

// ===================
// Property Components
// ===================

/**
 * Property details aggregate.
 */
export interface PropertyDetailsBlock {
  readonly property_type: string; // PropertyType enum value
  readonly structure?: LocalizedText;
  readonly room_layout: string; // RoomLayout enum value
  readonly area_sqm: number;
  readonly balcony_area_sqm?: number;
  readonly floor?: number;
  readonly total_floors?: number;
  readonly year_built?: number;
}

// ===================
// Amenities Components (Nested Structure)
// ===================

export interface BathroomAmenities {
  readonly separate: boolean;
  readonly dryer: boolean;
  readonly washlet: boolean;
  readonly reheating: boolean;
  readonly independent_vanity: boolean;
}

export interface KitchenAmenities {
  readonly system_kitchen: boolean;
  readonly dishwasher: boolean;
  readonly gas_stove: boolean;
  readonly ih_cooktop: boolean;
  readonly counter_kitchen: boolean;
}

export interface SecurityAmenities {
  readonly auto_lock: boolean;
  readonly video_intercom: boolean;
  readonly delivery_box: boolean;
  readonly security_camera: boolean;
}

export interface ContractAmenities {
  readonly pets_allowed: boolean;
  readonly foreigner_friendly: boolean;
  readonly instruments_ok: boolean;
  readonly soho_allowed: boolean;
}

export interface ComfortAmenities {
  readonly air_conditioning: boolean;
  readonly flooring_wood: boolean;
  readonly south_facing: boolean;
  readonly corner_unit: boolean;
}

export interface ConnectivityAmenities {
  readonly internet_included: boolean;
}

export interface ParkingAmenities {
  readonly parking: boolean;
  readonly bicycle_parking: boolean;
  readonly motorcycle_parking: boolean;
}

export interface LaundryAmenities {
  readonly washer_hookup: boolean;
  readonly washer_dryer_in_unit: boolean;
  readonly indoor_washer_space: boolean;
}

export interface BuildingAmenities {
  readonly elevator: boolean;
  readonly balcony: boolean;
}

/**
 * Amenities aggregate with semantic grouping.
 */
export interface AmenitiesBlock {
  readonly bathroom: BathroomAmenities;
  readonly kitchen: KitchenAmenities;
  readonly security: SecurityAmenities;
  readonly contract: ContractAmenities;
  readonly comfort: ComfortAmenities;
  readonly connectivity: ConnectivityAmenities;
  readonly parking: ParkingAmenities;
  readonly laundry: LaundryAmenities;
  readonly building: BuildingAmenities;
}

// ===================
// Media Components
// ===================

/**
 * Image with full-size and thumbnail URLs.
 */
export interface Image {
  readonly url: string;
  readonly thumbnail_url: string;
  readonly caption?: LocalizedText;
  readonly is_featured: boolean;
}

/**
 * Media assets aggregate (organized by section).
 */
export interface MediaBlock {
  readonly images_by_section: Record<string, readonly Image[]>;
}

// ===================
// Type Guards
// ===================

/**
 * Runtime check for LocalizedText interface.
 */
export function isLocalizedText(obj: unknown): obj is LocalizedText {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "ja" in obj &&
    "en" in obj &&
    "ru" in obj &&
    typeof (obj as LocalizedText).ja === "string" &&
    typeof (obj as LocalizedText).en === "string" &&
    typeof (obj as LocalizedText).ru === "string"
  );
}

/**
 * Runtime check for Currency interface.
 */
export function isCurrency(obj: unknown): obj is Currency {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "amount" in obj &&
    "currency" in obj &&
    typeof (obj as Currency).amount === "number" &&
    (obj as Currency).currency === "JPY"
  );
}

/**
 * Runtime check for GeoPoint interface.
 */
export function isGeoPoint(obj: unknown): obj is GeoPoint {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "lat" in obj &&
    "lng" in obj &&
    typeof (obj as GeoPoint).lat === "number" &&
    typeof (obj as GeoPoint).lng === "number"
  );
}
